import React, { useEffect, useState } from "react";
import { useForm } from "@mantine/form";
import { TextInput, NumberInput, Button, Group, Box, Textarea, Checkbox, Select, Alert } from "@mantine/core";
import HttpClient from "../helpers/HttpClient";
import { useNavigate } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import { Article } from "../types/article.type";
import { ArticleCategory } from "../types/article-category.type";
import { ArticleRemoveDialog } from "../components/ArticleRemoveDialog";
import { useLanguage } from "../providers/LanguageContext";

const ArticleEdit: React.FC<{ id?: number }> = ({ id }) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [isUpdate, setIsUpdate] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [isArchived, setIsArchived] = useState(false);
  const navigate = useNavigate();
  const httpClient = new HttpClient<Article>("/article");
  const [categories, setCategories] = useState<ArticleCategory[]>([]);

  const form = useForm({
    initialValues: {
      id: 0 as number | string,
      name: "",
      description: "",
      unitPrice: 0,
      memberPrice: undefined as number | undefined,
      isRestaurantArticle: false,
      isStockTracked: false,
      categoryId: null as string | null,
    },
    validate: {
      name: (v: string) => (v.trim() ? null : t.article.nameLabel + " ist erforderlich"),
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setCategories(await new HttpClient<ArticleCategory>("/article-category").getAll());
        if (id) {
          const result: any = await httpClient.getById(Number(id));
          form.setValues({
            ...result.data,
            unitPrice: result.data.unitPrice ?? 0,
            memberPrice: result.data.memberPrice ?? undefined,
            description: result.data.description ?? "",
            isRestaurantArticle: result.data.isRestaurantArticle ?? false,
            isStockTracked: result.data.isStockTracked ?? false,
            categoryId: result.data.category ? String(result.data.category.id) : null,
          });
          setIsArchived(!!result.data.isArchived);
          setIsUpdate(true);
        }
      } catch {
        notifications.show({ message: t.article.notFound, color: "red" });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) return <div>{t.common.loading}</div>;

  const onSubmit = async (values: typeof form.values) => {
    try {
      const { categoryId, ...rest } = values;
      const response = await httpClient.post({
        ...rest,
        category: categoryId ? { id: Number(categoryId) } : null,
      } as any);
      if (response.status !== 200 && response.status !== 201) throw new Error();
      notifications.show({ message: isUpdate ? t.article.updateSuccess : t.article.saveSuccess, color: "green" });
      navigate(-1);
    } catch {
      notifications.show({ message: t.article.saveFailed, color: "red" });
    }
  };

  const restore = async () => {
    try {
      await new HttpClient(`/article/${id}`).postAction("restore");
      setIsArchived(false);
      form.setFieldValue("isArchived" as never, false as never);
      notifications.show({ message: t.article.restored, color: "green" });
    } catch {
      notifications.show({ message: t.article.saveFailed, color: "red" });
    }
  };

  return (
    <>
      <ArticleRemoveDialog
        article={removeOpen && id ? { id: Number(id), name: form.values.name } : null}
        basePath="/article"
        onClose={() => setRemoveOpen(false)}
        onDone={() => navigate(-1)}
      />
      <Box mx="auto" maw={500} mt="lg">
        {isArchived && (
          <Alert color="orange" mb="md" title={t.article.archivedTitle}>
            {t.article.archivedMsg}
            <Button size="xs" variant="light" mt="xs" display="block" onClick={restore}>
              {t.article.restore}
            </Button>
          </Alert>
        )}
        <form onSubmit={form.onSubmit(onSubmit)}>
          <TextInput label={t.article.nameLabel} placeholder="Artikelname" mb="sm" {...form.getInputProps("name")} />
          <Textarea
            label={t.article.descriptionLabel}
            placeholder={t.article.descriptionPlaceholder}
            mb="sm"
            rows={3}
            {...form.getInputProps("description")}
          />
          <NumberInput
            label={t.article.unitPriceLabel}
            placeholder="0,00"
            allowDecimal
            decimalSeparator=","
            decimalScale={2}
            fixedDecimalScale
            mb="sm"
            {...form.getInputProps("unitPrice")}
          />
          <NumberInput
            label={t.article.memberPriceLabel}
            description={t.article.memberPriceDesc}
            placeholder="0,00"
            allowDecimal
            decimalSeparator=","
            decimalScale={2}
            fixedDecimalScale
            mb="sm"
            {...form.getInputProps("memberPrice")}
          />
          <Select
            label={t.article.categoryLabel}
            placeholder={t.article.categoryPlaceholder}
            clearable
            data={categories.map((c) => ({ value: String(c.id), label: c.name }))}
            mb="sm"
            {...form.getInputProps("categoryId")}
          />
          <Checkbox
            label={t.article.restaurantArticleLabel}
            description={t.article.restaurantArticleDesc}
            mb="md"
            {...form.getInputProps("isRestaurantArticle", { type: "checkbox" })}
          />
          <Checkbox
            label={t.article.stockTrackedLabel}
            description={t.article.stockTrackedDesc}
            mb="md"
            {...form.getInputProps("isStockTracked", { type: "checkbox" })}
          />
          <Group mt="md">
            <Button type="submit">{t.common.save}</Button>
            {isUpdate && !isArchived && (
              <Button type="button" color="red" variant="light" onClick={() => setRemoveOpen(true)}>
                {t.article.deleteOrArchive}
              </Button>
            )}
            <Button variant="subtle" onClick={() => navigate(-1)}>{t.common.cancel}</Button>
          </Group>
        </form>
      </Box>
    </>
  );
};

export default ArticleEdit;
